/**
 * Story 134 — how a BOM line is ORDERED and TYPED in the library's Lines tab.
 *
 * ⚠️ This module belongs to `bomLibrary/` on purpose. `bomTree.js` and `BomTreeView` are the workflow
 * EDITOR's, and story 117 left a bidirectional coupling between the two modules that this story is
 * explicitly forbidden to deepen: nothing here may be moved there, and nothing there may be reused.
 */

export const LINE_TYPE_ASSEMBLY = 'ASSEMBLY'
export const LINE_TYPE_RAW_MATERIAL = 'RAW_MATERIAL'
export const LINE_TYPE_COMMERCIAL = 'COMMERCIAL'

/** i18n key per derived type. `Assemblies` (plural) is the footer counter, not this. */
const TYPE_LABEL_KEYS = {
  [LINE_TYPE_ASSEMBLY]: 'Assembly',
  [LINE_TYPE_RAW_MATERIAL]: 'Raw material',
  [LINE_TYPE_COMMERCIAL]: 'Commercial'
}

const RAW_MATERIAL_RESOURCE_TYPE = 'RAW_MATERIAL_REFERENCE'

/**
 * Trailing ZERO segments do not mean a level: `"10.00"` and `"10"` are the same node, and `"10.10"`
 * is its child. This mirrors the rule the tree already applies (RN08.3) — it is restated here rather
 * than imported because the editor's module is not this story's to depend on beyond the tree builder
 * it already uses, and getting this wrong would silently mis-type every parent line as a leaf.
 */
export const normalizeBomPosition = (position) => {
  if (position === null || position === undefined || position === '') return ''
  const parts = String(position).split('.')
  while (parts.length > 1 && Number(parts[parts.length - 1]) === 0) {
    parts.pop()
  }
  return parts.join('.')
}

/**
 * Splits a position into its numeric segments. `"100.10"` → `[100, 10]`.
 *
 * A segment that is not a number sorts AFTER any number and then falls back to comparing as text, so
 * a hand-typed position never throws the whole tree into a random order.
 */
const segmentsOf = (position) =>
  normalizeBomPosition(position)
    .split('.')
    .map((segment) => {
      const trimmed = segment.trim()
      const numeric = Number(trimmed)
      return { numeric: trimmed !== '' && Number.isFinite(numeric) ? numeric : null, text: trimmed }
    })

/**
 * RN-134-17 — positions are compared SEGMENT BY SEGMENT AS NUMBERS: `20` comes before `100.10`.
 * Sorting them as text would put `100.10` first, which is the bug this rule exists to prevent.
 * A shorter position that is a prefix of a longer one comes first: the parent before its children.
 */
export const compareBomPositions = (a, b) => {
  const left = segmentsOf(a)
  const right = segmentsOf(b)
  const shared = Math.min(left.length, right.length)

  for (let i = 0; i < shared; i++) {
    const l = left[i]
    const r = right[i]
    if (l.numeric !== null && r.numeric !== null) {
      if (l.numeric !== r.numeric) return l.numeric - r.numeric
    } else if (l.numeric !== null) {
      return -1 // a number sorts before anything that is not one
    } else if (r.numeric !== null) {
      return 1
    } else if (l.text !== r.text) {
      return l.text < r.text ? -1 : 1
    }
  }
  return left.length - right.length
}

/** The lines of a version, in the order the tree paints them. Does not mutate the input. */
export const sortBomLines = (lines) =>
  [...(lines || [])].sort((a, b) => compareBomPositions(a && a.position, b && b.position))

/**
 * True when some other line hangs below this position, i.e. the line is a node and not a leaf.
 * Both sides are normalised first, so `"10.00"` is correctly seen as the parent of `"10.10"`.
 */
export const hasChildren = (position, lines) => {
  const normalized = normalizeBomPosition(position)
  if (normalized === '') return false
  const prefix = normalized + '.'
  return (lines || []).some((line) => {
    const candidate = normalizeBomPosition(line && line.position)
    return candidate !== normalized && candidate.startsWith(prefix)
  })
}

/**
 * D10 — the three types the Lines tab shows, DERIVED, never read from a field:
 *
 * - `Assembly`  — the line has descendants. It is consumed through its children.
 * - `Raw material` — the catalogue it points at is the raw-material one.
 * - `Commercial` — everything else.
 *
 * ⚠️ "Commercial" is a LABEL, not a fact about the part: manufactured parts land in this bucket too,
 * because the system has no datum that tells a bought part from a made one. Signed knowing it (D10);
 * do not invent a fourth type to "fix" it.
 */
export const bomLineType = (line, lines) => {
  if (hasChildren(line && line.position, lines)) {
    return LINE_TYPE_ASSEMBLY
  }
  const resourceType = (line && (line.resourceTypeName || line.resourceType)) || ''
  if (String(resourceType).trim().toUpperCase() === RAW_MATERIAL_RESOURCE_TYPE) {
    return LINE_TYPE_RAW_MATERIAL
  }
  return LINE_TYPE_COMMERCIAL
}

export const bomLineTypeLabelKey = (line, lines) => TYPE_LABEL_KEYS[bomLineType(line, lines)]

export const bomLineTypeLabel = (line, lines, t_) => t_(bomLineTypeLabelKey(line, lines))

/**
 * D4 / RN-134-21 — the footer of the Lines tab: a FLAT sum of every line, without multiplying a child
 * by its parent and without grouping by unit.
 *
 * ⚠️ This is NOT the denominator of the coverage (S48). The coverage counts only the LEAVES (D5), so
 * the two numbers are different on purpose and must never be presented as the same thing.
 */
export const bomLinesFooter = (lines) => {
  const all = lines || []
  let units = 0
  let assemblies = 0
  all.forEach((line) => {
    const quantity = Number(line && line.quantity)
    units += Number.isFinite(quantity) ? quantity : 0
    if (bomLineType(line, all) === LINE_TYPE_ASSEMBLY) assemblies += 1
  })
  return { lines: all.length, assemblies, units }
}
